// src/components/accueil/AccueilCoordinateur.tsx — SCE Issues
// Accueil V2 du coordinateur (Lot 9, maquette validée le 25/09) : ce qui
// l'attend dans SES métiers. L'administrateur le voit sur tous les métiers.
//
//   Bureau (≥ 1100 px) : quatre compteurs, puis trois colonnes à hauteur
//   d'écran qui défilent chacune seule — la file choisie, l'activité,
//   les éléments suivis et ses déclarations.
//
// Lot 12 : « Problèmes suivis » remplace « Équipements récurrents » (déjà dans
// les statistiques) ; l'activité de ses métiers inclut aussi les problèmes
// qu'il suit ailleurs. Lot 12 ter : devenue « Éléments suivis »
// (CarteElementsSuivis), équipements suivis compris.
//   Téléphone : les compteurs deviennent des onglets, cinq lignes par liste
//   puis « Voir les N » ; pas de défilement dans le défilement.
//
// Mon accueil, mes files (03/11, docs/chantier-accueil.md) : les colonnes et
// l'ordre des files viennent de la disposition (modèle du rôle + différences de
// la personne, useDispositionAccueil). Le modèle du code est l'accueil d'avant.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useChoixDeclaration } from '@/components/layout/ChoixDeclaration'
import { ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { METIERS_COULEURS, METIERS_ORDRE } from '@/config/metiers.config'
import { SUJETS_CONFIG, libelleCle, rangeeMetiersEtSujets, type ClePerimetre } from '@/config/sujets.config'
import { SUJETS, estSujet, type Issue } from '@/types/issue.types'

/** Point de couleur d'un métier, ou du métier qui traite un sujet (Goulotte : Méca). */
const couleurCle = (c: ClePerimetre): string => METIERS_COULEURS[estSujet(c) ? SUJETS_CONFIG[c].metier : c]
import { problemesSuivis } from '@/utils/abonnements'
import type { ElementsSuivis } from '@/utils/elementsSuivis'
import {
  dernieresActivites,
  evenementsAccueil,
  filesCoordinateur,
  grouperParMachine,
  machinesDeLaLigne,
  joursDepuis,
  JOURS_SANS_ACTIVITE,
  libelleAge,
  nouveautesParProbleme,
  regrouperFil,
  salutation,
  type FileCoordinateur,
  type IdFile,
} from '@/utils/accueil'
import type { DonneesAccueil } from '@/hooks/useAccueil'
import type { ListeProblemes } from '@/components/dashboard/ListeProblemesSheet'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { CarteAccueil, PiedCarte, SqueletteLignes, VideCarte } from './CarteAccueil'
import { aDesSuivis, CarteElementsSuivis } from './CarteElementsSuivis'
import { CompteursFile } from './CompteursFile'
import { FilActivite } from './FilActivite'
import { CarteActionsFnct } from './CarteActionsFnct'
import { CarteRevueFnct } from './CarteRevueFnct'
import { LigneProbleme, PastilleAge } from './LigneProbleme'
import { RechercheGlobale } from './RechercheGlobale'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { usePreferenceLocale } from '@/hooks/usePreferenceLocale'
import { BoutonParMachine, pluriel, TitreGroupeMachine } from './GroupeMachine'
import { useDispositionAccueil } from '@/hooks/useDispositionAccueil'
import { ajouterCarte, cartesAuTelephone, CARTES_ACCUEIL, carteDeFile, disposer, enAlerte, estGrande, estIdCarte, fileDeCarte, libelleAlerte, type Rangement } from '@/utils/dispositionAccueil'
import { fileDeVue, joursDeSilence, nouvelleFile, ORDRES_FILE, type FileComposee } from '@/utils/filesComposees'
import { useFilesComposees, type FileCalculee } from '@/hooks/useFilesComposees'
import { ComposerFile } from './personnaliser/ComposerFile'
import { ReglageTelephone } from './personnaliser/ReglageTelephone'
import { LayoutDashboard } from 'lucide-react'
import { carteDisponible } from './disponibiliteCartes'
import { ColonnesEditables } from './personnaliser/ColonnesEditables'
import { BarrePersonnaliser, BoutonPersonnaliser } from './personnaliser/BarrePersonnaliser'
import { FilesEditables } from './personnaliser/FilesEditables'

const LIGNES_TELEPHONE = 5
const CLE_PERIMETRE = 'sce-issues.accueil.perimetre-activite'

// Hauteur des colonnes au bureau : l'écran moins l'en-tête (80), la barre du
// bas (68), le bandeau de bascule de rôle s'il est là, et le haut de la page
// (bonjour + compteurs + marges : 210 px, réduit de 250 le 30/09 — il restait
// un vide sous les colonnes). Jamais moins de 440 px : sur un petit écran, la
// page défile plutôt que d'écraser les colonnes.
const HAUTEUR_COLONNES =
  'max(440px, calc(100dvh - var(--spacing-header-h) - 68px - var(--hauteur-bandeau-vue, 0px) - 210px))'

/** Une file de l'accueil : une des quatre du code, ou une file composée (lot 5). */
type FileAccueil = FileCoordinateur | FileCalculee
const estComposee = (f: FileAccueil): f is FileCalculee => 'composee' in f

const STYLE_GRILLE: CSSProperties = { display: 'grid', gridTemplateColumns: '1.25fr 1fr 0.95fr', gridTemplateRows: 'minmax(0, 1fr)', gap: 20, height: HAUTEUR_COLONNES }

const CLE_METIER = 'sce-issues.accueil.metier-choisi'

function lireMetierChoisi(): string | null {
  try {
    return localStorage.getItem(CLE_METIER)
  } catch {
    return null
  }
}

function lirePerimetre(): 'metiers' | 'tout' {
  try {
    return localStorage.getItem(CLE_PERIMETRE) === 'tout' ? 'tout' : 'metiers'
  } catch {
    return 'metiers'
  }
}

export function AccueilCoordinateur({
  issues,
  loading,
  accueil,
  metiers,
  prenom,
  moi,
  monId,
  upn,
  elementsSuivis,
  maintenant,
  large,
  ouvrirListe,
}: {
  issues: Issue[]
  loading: boolean
  accueil: DonneesAccueil
  /** Métiers pilotés et sujets dont il est responsable (23/10) ; vide = tous (administrateur). */
  metiers: ClePerimetre[]
  prenom: string
  /** Nom affiché, pour « Vous » dans l'activité. */
  moi: string
  /** Identifiant SharePoint de la personne affichée (abonnements, Lot 12). */
  monId: number | null
  upn: string
  /** PN et N° de série suivis (Lots 12 bis et ter) : leurs problèmes actifs s'ajoutent aux suivis. */
  elementsSuivis: ElementsSuivis
  maintenant: number
  large: boolean
  ouvrirListe: (liste: ListeProblemes) => void
}): JSX.Element {
  const choix = useChoixDeclaration()
  const [choisi, setChoisi] = useState<string>('aQualifier')
  const [perimetre, setPerimetre] = useState<'metiers' | 'tout'>(lirePerimetre)
  const [toutVoir, setToutVoir] = useState(false)
  // « Par machine » (02/10) : retenu dans ce navigateur, une bascule par carte.
  const [fileParMachine, setFileParMachine] = usePreferenceLocale('sce-issues.accueil.file-par-machine', false)
  const [filParMachine, setFilParMachine] = usePreferenceLocale('sce-issues.accueil.activite-par-machine', false)
  const [fileRepliees, setFileRepliees] = useState<ReadonlySet<string>>(new Set())
  const [filRepliees, setFilRepliees] = useState<ReadonlySet<string>>(new Set())
  const basculer = (set: (f: (p: ReadonlySet<string>) => ReadonlySet<string>) => void) => (cle: string): void =>
    set((p) => {
      const n = new Set(p)
      if (n.has(cle)) n.delete(cle)
      else n.add(cle)
      return n
    })

  function changerPerimetre(p: 'metiers' | 'tout'): void {
    setPerimetre(p)
    try { localStorage.setItem(CLE_PERIMETRE, p) } catch { /* préférence de confort */ }
  }

  // Un seul de MES métiers (15/10), retenu dans ce navigateur. Une valeur qui
  // n'est plus dans mon périmètre (bascule de rôle, changement de métier) est
  // ignorée : on retombe sur tous. L'administrateur (aucun métier = tous)
  // choisit parmi les quatre (19/10), et les sujets (23/10).
  const estAdministrateur = metiers.length === 0
  const metiersAuChoix: readonly ClePerimetre[] = estAdministrateur ? rangeeMetiersEtSujets(METIERS_ORDRE, SUJETS) : metiers
  const [metierMemorise, setMetierMemorise] = useState<string | null>(lireMetierChoisi)
  const metierChoisi = metiersAuChoix.find((m) => m === metierMemorise) ?? null
  const metiersActifs = useMemo(() => (metierChoisi ? [metierChoisi] : metiers), [metierChoisi, metiers])
  function choisirMetier(m: ClePerimetre | null): void {
    setMetierMemorise(m)
    try {
      if (m) localStorage.setItem(CLE_METIER, m)
      else localStorage.removeItem(CLE_METIER)
    } catch { /* préférence de confort */ }
  }
  const selecteurMetiers = metiersAuChoix.length > 1
    ? <SelecteurMetiers metiers={metiersAuChoix} choisi={metierChoisi} onChoisir={choisirMetier} />
    : null
  // « Mes métiers / Tout » n'a pas de sens pour l'administrateur : ses métiers
  // sont tous. Le métier choisi ci-dessus vaut aussi pour l'activité.
  const perimetreActivite = estAdministrateur ? 'metiers' : perimetre

  // Les cartes et l'ordre des files : modèle du rôle + différences de la personne.
  const disponible = useCallback((id: string) => carteDisponible(id, upn, false), [upn])
  const dispo = useDispositionAccueil(estAdministrateur ? 'administrateur' : 'coordinateur', disponible)
  // Mode « Personnaliser » (lot 3, bureau) : un brouillon, enregistré à « Terminé ».
  const [brouillon, setBrouillon] = useState<Rangement | null>(null)
  const disposition: Rangement = brouillon ?? dispo.disposition
  const modeleSeul = useMemo(() => disposer(dispo.modele, null, disponible), [dispo.modele, disponible])
  const rangementModele: Rangement = { colonnes: modeleSeul.colonnes, files: modeleSeul.files, reglagesFiles: {}, filesComposees: [] }
  // Le composeur (lot 5) : une file à modifier, ou une nouvelle.
  const [composeur, setComposeur] = useState<FileComposee | null>(null)
  // « Mon accueil au téléphone » (lot 7).
  const [reglageTel, setReglageTel] = useState(false)
  const auModele = !!brouillon && JSON.stringify(brouillon) === JSON.stringify(rangementModele)
  const reglagesFiles = disposition.reglagesFiles
  const delai = reglagesFiles.sansActivite?.delai ?? JOURS_SANS_ACTIVITE
  useEffect(() => {
    if (!large) setBrouillon(null)
  }, [large])

  const activites = useMemo(() => dernieresActivites(issues, accueil.dates ?? []), [issues, accueil.dates])
  const toutesLesFiles = useMemo(
    () => filesCoordinateur(issues, metiersActifs, activites, maintenant, delai),
    [issues, metiersActifs, activites, maintenant, delai],
  )
  // Tant que les dates ne sont pas lues, « sans activité » serait faux
  // (tout ce qui est en cours depuis 7 jours y tomberait) : on n'affiche rien.
  const datesPretes = accueil.dates !== null

  const suivis = useMemo(
    () => problemesSuivis(issues, monId, upn, activites, elementsSuivis),
    [issues, monId, upn, activites, elementsSuivis],
  )
  const idsSuivis = useMemo(() => new Set(suivis.map((i) => i.id)), [suivis])

  // Les files composées (lot 5), calculées comme les vues du tableau.
  const composees = useFilesComposees({
    composees: disposition.filesComposees,
    issues,
    activites,
    maintenant,
    idsSuivis,
    lireVues: !!brouillon,
  })
  const toutes: FileAccueil[] = useMemo(() => [...toutesLesFiles, ...composees.files], [toutesLesFiles, composees.files])
  const files = useMemo(() => {
    const rangees = disposition.files.flatMap((id) => toutes.filter((f) => f.id === id))
    return rangees.length ? rangees : toutesLesFiles
  }, [disposition.files, toutes, toutesLesFiles])
  const file = files.find((f) => f.id === choisi) ?? files[0]!
  // Seuils d'alerte (lot 4) : point rouge sur le compteur.
  const alertes = useMemo(
    () => new Map(files.filter((f) => enAlerte(f.issues.length, f.attenteJours, reglagesFiles[f.id])).map((f) => [f.id, libelleAlerte(reglagesFiles[f.id])])),
    [files, reglagesFiles],
  )
  // Pour une file composée : l'aperçu du composeur, sur les problèmes du moment.
  const ajouterOuRemplacer = (f: FileComposee): void => {
    if (!brouillon) return
    const existe = brouillon.filesComposees.some((x) => x.id === f.id)
    setBrouillon({
      ...brouillon,
      filesComposees: existe ? brouillon.filesComposees.map((x) => (x.id === f.id ? f : x)) : [...brouillon.filesComposees, f],
      files: brouillon.files.includes(f.id) ? brouillon.files : [...brouillon.files, f.id],
    })
  }

  const evenements = useMemo(
    () => evenementsAccueil(
      accueil.evenements,
      issues,
      perimetreActivite === 'metiers' ? metiersActifs : [],
      accueil.visitePrecedente,
      idsSuivis,
    ),
    [accueil.evenements, issues, perimetreActivite, metiersActifs, accueil.visitePrecedente, idsSuivis],
  )
  // Ce qu'un autre a fait sur mes suivis depuis la dernière visite.
  const nouveautesSuivis = useMemo(
    () => (accueil.visiteLue ? nouveautesParProbleme(evenements, idsSuivis, moi) : new Map()),
    [evenements, idsSuivis, moi, accueil.visiteLue],
  )
  const bonjour = salutation(prenom, maintenant, accueil.visitePrecedente, accueil.retour?.retour ?? null)
  const libelleMetiers = metiersActifs.length ? metiersActifs.map(libelleCle).join(' + ') : 'tous les métiers'

  const listeDeLaFile = (f: FileAccueil): ListeProblemes => ({
    titre: f.libelle,
    contexte: libelleMetiers,
    issues: f.issues,
    // « À qualifier » : tout est Ouvert et à qualifier, seule l'ancienneté distingue.
    colonne: f.id === 'aQualifier' ? 'age' : 'statut',
  })

  // ─── Blocs ───

  // Une file en lignes : la file choisie, ou une file affichée en carte (lot 4,
  // sans regroupement par machine : la bascule est celle de la file choisie).
  const lignesDe = (file: FileAccueil, limite?: number, groupable = true): JSX.Element => {
    if (loading && issues.length === 0) return <SqueletteLignes />
    if (file.id === 'sansActivite' && !datesPretes) return <SqueletteLignes n={2} />
    if (file.issues.length === 0) return <VideCarte>{estComposee(file) ? 'Aucun problème dans cette file aujourd’hui.' : vide(file.id, delai)}</VideCarte>
    const montres = limite ? file.issues.slice(0, limite) : file.issues
    const ligne = (i: Issue, avecMachine: boolean): JSX.Element => (
      <LigneProbleme
        key={i.id}
        issue={i}
        deuxLignes={!large}
        meta={[i.id, avecMachine ? machinesDeLaLigne(i, file.machines) : '', large ? i.declarantNom : ''].filter(Boolean).join(' · ')}
        droite={<DroiteFile id={estComposee(file) ? 'composee' : file.id} issue={i} activites={activites} maintenant={maintenant} large={large} delai={delai} />}
      />
    )
    if (fileParMachine && groupable) {
      const groupes = grouperParMachine(montres, (i) => i)
      // La fiche parcourt la file dans l'ordre affiché, groupes repliés exclus.
      const suite = groupes.flatMap((g) => (fileRepliees.has(g.cle) ? [] : g.items))
      return (
        <SuiteListe libelle={file.libelle} issues={suite}>
          {groupes.map((g) => (
            <div key={g.cle}>
              <TitreGroupeMachine
                cle={g.cle}
                pn={g.pn}
                detail={pluriel(g.items.length, 'problème')}
                replie={fileRepliees.has(g.cle)}
                onBascule={() => basculer(setFileRepliees)(g.cle)}
              />
              {/* Le N° de série est dans le titre : seules les autres machines d'un défaut commun restent écrites. */}
              {!fileRepliees.has(g.cle) && g.items.map((i) => ligne(i, (file.machines?.get(i.id) ?? 1) > 1))}
            </div>
          ))}
        </SuiteListe>
      )
    }
    // La fiche parcourt toute la file, pas seulement les lignes montrées.
    return (
      <SuiteListe libelle={file.libelle} issues={file.issues}>
        {montres.map((i) => ligne(i, true))}
      </SuiteListe>
    )
  }
  const lignesFile = (limite?: number): JSX.Element => lignesDe(file, limite)

  const selecteurPerimetre = (
    <div role="radiogroup" aria-label="Périmètre de l'activité" className="flex" style={{ backgroundColor: 'var(--color-gray-5)', borderRadius: 8, padding: 3, flexShrink: 0 }}>
      {(['metiers', 'tout'] as const).map((p) => (
        <button
          key={p}
          type="button"
          role="radio"
          aria-checked={perimetre === p}
          onClick={() => changerPerimetre(p)}
          style={{
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: perimetre === p ? 500 : 400,
            padding: '5px 10px',
            minHeight: 32,
            borderRadius: 6,
            border: perimetre === p ? '1px solid var(--color-black)' : '1px solid transparent',
            backgroundColor: perimetre === p ? 'var(--color-yellow)' : 'transparent',
            cursor: 'pointer',
          }}
        >
          {p === 'metiers' ? (metiers.length ? 'Mes métiers' : 'Tous') : 'Tout'}
        </button>
      ))}
    </div>
  )

  const actionsActivite = (
    <div className="flex items-center" style={{ gap: 8 }}>
      <BoutonParMachine actif={filParMachine} onChange={setFilParMachine} compact />
      {!estAdministrateur && selecteurPerimetre}
    </div>
  )

  const fil = (limite?: number): JSX.Element => {
    if (accueil.loading && accueil.evenements.length === 0) return <SqueletteLignes />
    if (accueil.error && accueil.evenements.length === 0) return <VideCarte erreur>{accueil.error}</VideCarte>
    if (evenements.length === 0) return <VideCarte>Aucune activité récente.</VideCarte>
    return (
      <FilActivite
        evenements={evenements}
        limite={limite}
        visitePrecedente={accueil.visiteLue ? accueil.visitePrecedente : null}
        moi={moi}
        maintenant={maintenant}
        parMachine={filParMachine}
        repliees={filRepliees}
        onBasculer={basculer(setFilRepliees)}
      />
    )
  }

  const carteSuivis = (
    <CarteElementsSuivis
      issues={issues}
      suivis={suivis}
      elements={elementsSuivis}
      nouveautes={nouveautesSuivis}
      monId={monId}
      upn={upn}
      maintenant={maintenant}
      loading={loading}
      large={large}
      ajuste={large}
      ouvrirListe={ouvrirListe}
    />
  )

  // Le titre d'une carte, pour les réglages (catalogue, téléphone).
  const titreCarte = (id: string): string =>
    estIdCarte(id) ? CARTES_ACCUEIL[id].titre : toutes.find((f) => f.id === fileDeCarte(id))?.libelle ?? id

  // ─── Téléphone ───
  // Une colonne (lot 7) : les cartes du bureau dans l'ordre du modèle — celui
  // d'avant le chantier — ou dans celui de la personne, sans celles qu'elle a
  // retirées du téléphone.

  const carteTelephone = (id: string): JSX.Element | null => {
    switch (id) {
      case 'file':
        return (
          <section key={id} className="flex flex-col" style={{ gap: 10 }}>
            <CompteursFile files={files} choisi={choisi} onChoisir={setChoisi} variante="onglets" enAttente={datesPretes ? [] : ['sansActivite']} alertes={alertes} />
            <CarteAccueil
              titre={file.libelle}
              compteur={file.issues.length}
              action={<BoutonParMachine actif={fileParMachine} onChange={setFileParMachine} />}
              pied={file.issues.length > LIGNES_TELEPHONE ? (
                <PiedCarte libelle={`Voir les ${file.issues.length}`} onClick={() => ouvrirListe(listeDeLaFile(file))} />
              ) : undefined}
            >
              {lignesFile(LIGNES_TELEPHONE)}
            </CarteAccueil>
          </section>
        )
      case 'revueFnct':
        return <CarteRevueFnct key={id} />
      case 'actionsFnct':
        return <CarteActionsFnct key={id} />
      case 'activite':
        return (
          <CarteAccueil
            key={id}
            titre="Activité récente"
            action={actionsActivite}
            pied={(filParMachine ? evenements.length : regrouperFil(evenements).length) > 6 ? (
              <PiedCarte libelle={toutVoir ? 'Réduire' : 'Toute l\'activité récente'} onClick={() => setToutVoir((v) => !v)} />
            ) : undefined}
          >
            {fil(toutVoir ? undefined : 6)}
          </CarteAccueil>
        )
      case 'suivis':
        return aDesSuivis(suivis, elementsSuivis) ? <div key={id}>{carteSuivis}</div> : null
      default: {
        const f = toutes.find((x) => x.id === fileDeCarte(id))
        if (!f) return null
        return (
          <CarteAccueil
            key={id}
            titre={f.libelle}
            compteur={f.issues.length}
            pied={f.issues.length > LIGNES_TELEPHONE ? <PiedCarte libelle={`Voir les ${f.issues.length}`} onClick={() => ouvrirListe(listeDeLaFile(f))} /> : undefined}
          >
            {lignesDe(f, LIGNES_TELEPHONE, false)}
          </CarteAccueil>
        )
      }
    }
  }

  if (!large) {
    return (
      <div className="flex flex-col" style={{ gap: 18, padding: '14px 16px 24px' }}>
        <RechercheGlobale issues={issues} variante="page" />
        <div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {bonjour} · {libelleMetiers}
          </div>
          <div className="flex items-center justify-between" style={{ gap: 8 }}>
            <h1 style={{ margin: '2px 0 0', fontSize: 25, fontWeight: 400, lineHeight: '30px' }}>Ce qui vous attend</h1>
            {dispo.modifiable && (
              <button type="button" className="perso-menu" aria-label="Mon accueil au téléphone" title="Mon accueil au téléphone" onClick={() => setReglageTel(true)}>
                <LayoutDashboard size={20} aria-hidden="true" />
              </button>
            )}
          </div>
          {selecteurMetiers && <div style={{ marginTop: 10, minWidth: 0 }}>{selecteurMetiers}</div>}
        </div>

        {cartesAuTelephone(dispo.disposition.colonnes, dispo.modele, dispo.disposition.telephone).map(carteTelephone)}

        {reglageTel && (
          <ReglageTelephone
            colonnes={dispo.disposition.colonnes}
            modele={dispo.modele}
            reglage={dispo.disposition.telephone}
            titreDe={titreCarte}
            onFermer={() => setReglageTel(false)}
            onTerminer={(telephone) => {
              dispo.enregistrer({ ...dispo.disposition, telephone })
              setReglageTel(false)
            }}
          />
        )}
      </div>
    )
  }

  // ─── Bureau ───

  // Une grande carte (file, activité) prend la hauteur qui reste et défile ;
  // une petite prend la hauteur de son contenu, plafonnée si elle partage sa colonne.
  function placeCarte(id: string, seule: boolean): CSSProperties {
    const def = estIdCarte(id) ? CARTES_ACCUEIL[id] : null
    if (estGrande(id) || seule) return { flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }
    return { flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', ...(def?.hauteurMax ? { maxHeight: def.hauteurMax, flexShrink: 0 } : {}) }
  }

  function carteBureau(id: string): JSX.Element | null {
    switch (id) {
      case 'file':
        return (
          <CarteAccueil
            defile
            titre={file.libelle}
            compteur={file.issues.length}
            sousTitre={sousTitre(file)}
            action={<BoutonParMachine actif={fileParMachine} onChange={setFileParMachine} />}
            pied={file.issues.length > 0 ? (
              <PiedCarte libelle="Ouvrir en liste" onClick={() => ouvrirListe(listeDeLaFile(file))} />
            ) : undefined}
          >
            {lignesFile()}
          </CarteAccueil>
        )
      case 'activite':
        return (
          <CarteAccueil defile titre="Activité récente" action={actionsActivite}>
            {fil()}
          </CarteAccueil>
        )
      // Le plafond de hauteur est posé par placeCarte ; la carte s'y serre et défile.
      case 'revueFnct':
        return <CarteRevueFnct hauteurMax="100%" />
      case 'actionsFnct':
        return <CarteActionsFnct hauteurMax="100%" />
      case 'suivis':
        return carteSuivis
      default: {
        // Une file affichée en carte (lot 4) : `file:critiques`.
        const f = toutes.find((x) => x.id === fileDeCarte(id))
        if (!f) return null
        return (
          <CarteAccueil
            defile
            titre={f.libelle}
            compteur={f.issues.length}
            sousTitre={sousTitre(f)}
            pied={f.issues.length > 0 ? <PiedCarte libelle="Ouvrir en liste" onClick={() => ouvrirListe(listeDeLaFile(f))} /> : undefined}
          >
            {lignesDe(f, undefined, false)}
          </CarteAccueil>
        )
      }
    }
  }

  return (
    <div className="flex flex-col" style={{ gap: 16, padding: '22px 40px 24px' }}>
      <div className="flex items-end justify-between" style={{ gap: 24 }}>
        <div>
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            {/* Pas de rôle ici : un suppléant n'est pas « coordinateur », et les
                pastilles métier sous le titre disent déjà le périmètre. */}
            {bonjour}
          </div>
          <div className="flex items-center flex-wrap" style={{ gap: 12, marginTop: 4 }}>
            <h1 style={{ margin: 0, fontSize: 29, fontWeight: 400, lineHeight: '34px' }}>Ce qui vous attend</h1>
            {selecteurMetiers ?? metiers.map((m) => <PuceMetier key={m} metier={m} />)}
            {!selecteurMetiers && metiers.length === 0 && (
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>tous les métiers</span>
            )}
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 10 }}>
          {dispo.modifiable && !brouillon && (
            <BoutonPersonnaliser onClick={() => setBrouillon({ colonnes: dispo.disposition.colonnes, files: dispo.disposition.files, reglagesFiles: dispo.disposition.reglagesFiles, filesComposees: dispo.disposition.filesComposees })} />
          )}
          <Button variant="primary" size="lg" iconLeft={<Plus size={18} strokeWidth={2.5} />} onClick={choix.basculer}>
            Déclarer
          </Button>
        </div>
        {choix.feuille}
      </div>

      {composeur && brouillon && (
        <ComposerFile
          initiale={composeur}
          metiers={metiersAuChoix}
          nomsPris={toutes.filter((f) => f.id !== composeur.id).map((f) => f.libelle)}
          calculer={composees.calculer}
          onAnnuler={() => setComposeur(null)}
          onValider={(f) => {
            ajouterOuRemplacer(f)
            setComposeur(null)
          }}
        />
      )}

      {brouillon && (
        <BarrePersonnaliser
          auModele={auModele}
          onModele={() => setBrouillon(rangementModele)}
          onAnnuler={() => setBrouillon(null)}
          onTerminer={() => {
            dispo.enregistrer(brouillon)
            setBrouillon(null)
          }}
        />
      )}

      {brouillon ? (
        <FilesEditables
          files={brouillon.files}
          toutes={toutes.map((f) => ({ id: f.id, libelle: f.libelle, nombre: f.issues.length }))}
          reglages={brouillon.reglagesFiles}
          modele={dispo.modele}
          cartes={brouillon.colonnes.flat()}
          onFiles={(f) => setBrouillon({ ...brouillon, files: f })}
          onReglages={(r) => setBrouillon({ ...brouillon, reglagesFiles: r })}
          onAfficherEnCarte={(id) => setBrouillon({ ...brouillon, colonnes: ajouterCarte(brouillon.colonnes, carteDeFile(id), dispo.modele, 0) })}
          composeeDe={(id) => brouillon.filesComposees.find((f) => f.id === id)}
          vues={composees.vues}
          onComposer={(f) => setComposeur(f ?? nouvelleFile(metiersActifs))}
          onEpingler={(v) => ajouterOuRemplacer(fileDeVue(v))}
        />
      ) : (
        <CompteursFile files={files} choisi={choisi} onChoisir={setChoisi} variante="tuiles" enAttente={datesPretes ? [] : ['sansActivite']} alertes={alertes} nouvelles={dispo.nouvelles} />
      )}

      {brouillon ? (
        <ColonnesEditables
          colonnes={brouillon.colonnes}
          modele={dispo.modele}
          disponible={(id) => disponible(id) || brouillon.filesComposees.some((f) => f.id === fileDeCarte(id))}
          files={brouillon.files}
          titre={(id) => toutes.find((f) => f.id === fileDeCarte(id))?.libelle ?? id}
          onChange={(colonnes) => setBrouillon({ ...brouillon, colonnes })}
          rendre={carteBureau}
          place={placeCarte}
          style={STYLE_GRILLE}
        />
      ) : (
      <div style={STYLE_GRILLE}>
        {/* Modèle du code : la file | l'activité | revue, actions FNC-T et
            éléments suivis (les coordinateurs retrouvent leurs déclarations
            dans « Tous les problèmes », filtre « Mes déclarations »). */}
        {disposition.colonnes.map((colonne, j) => (
          <div key={j} className="flex flex-col" style={{ minHeight: 0, gap: 20 }}>
            {/* .place-carte vide (carte sans contenu : pas d'action FNC-T) : retirée, pas d'écart en trop. */}
            {colonne.map((id) => (
              <div key={id} className="place-carte" style={{ ...placeCarte(id, colonne.length === 1), position: 'relative' }}>
                {carteBureau(id)}
                {/* Arrivée du modèle (lot 6) : le temps de la visite. */}
                {dispo.nouvelles.has(id) && <span className="carte-nouvelle">Nouveau</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      )}
    </div>
  )
}

// ─── Morceaux ───

function vide(id: IdFile, delai: number): string {
  switch (id) {
    case 'aQualifier':
      return 'Rien à qualifier. Tout est pris en main.'
    case 'critiques':
      return 'Aucun problème critique en cours.'
    case 'sansActivite':
      return `Aucun problème en cours sans activité depuis ${delai} jours.`
    case 'enCours':
      return 'Aucun problème en cours.'
    default: {
      const inconnu: never = id
      return String(inconnu)
    }
  }
}

function sousTitre(f: FileAccueil): string {
  if (!estComposee(f)) return SOUS_TITRES[f.id]
  if (f.composee.vue) return 'Dans l’ordre de la vue'
  return ORDRES_FILE.find((o) => o.ordre === f.composee.ordre)?.libelle ?? ''
}

const SOUS_TITRES: Record<IdFile, string> = {
  aQualifier: 'Du plus ancien au plus récent',
  critiques: 'Du plus ancien au plus récent',
  sansActivite: 'Du plus long silence au plus court',
  enCours: 'Du plus long silence au plus court',
}

function DroiteFile({
  id,
  issue,
  activites,
  maintenant,
  large,
  delai,
}: {
  id: IdFile | 'composee'
  issue: Issue
  activites: ReadonlyMap<string, number>
  maintenant: number
  large: boolean
  /** « Sans activité depuis » réglé (3, 7 ou 14 j) : au-delà, la pastille d'un « en cours » passe au rouge. */
  delai: number
}): JSX.Element {
  // Une file composée (lot 5) : l'état, puis les jours de silence.
  if (id === 'composee') {
    const silence = joursDeSilence(issue, { activites, maintenant })
    return (
      <>
        {large && <EtatProbleme statut={issue.statut} criticite={issue.criticite} />}
        <PastilleAge texte={`${silence} j`} enRetard={silence >= delai} />
      </>
    )
  }
  const derniere = activites.get(issue.id)
  const silence = derniere ? Math.floor((maintenant - derniere) / 86_400_000) : 0
  switch (id) {
    case 'aQualifier':
      return (
        <>
          <PastilleAge
            texte={libelleAge(issue.dateDeclaration, maintenant)}
            enRetard={joursDepuis(issue.dateDeclaration, maintenant) >= JOURS_SANS_ACTIVITE}
          />
          {/* Un chevron, pas un bouton « Qualifier » : toute la ligne ouvre la fiche, où l'on qualifie. */}
          {large && <ChevronRight size={18} color="var(--color-gray-60)" aria-hidden="true" />}
        </>
      )
    case 'critiques':
      return <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
    case 'sansActivite':
      return <PastilleAge texte={`${silence} j`} enRetard />
    case 'enCours':
      return (
        <>
          {large && <EtatProbleme statut={issue.statut} criticite={issue.criticite} />}
          <PastilleAge texte={derniere ? `${silence} j` : ''} enRetard={silence >= delai} />
        </>
      )
    default: {
      const inconnu: never = id
      return <>{String(inconnu)}</>
    }
  }
}

/**
 * Un seul de SES métiers, ou tous (15/10) : Élec + Autom (Thibaut, Hugo) ou
 * MECA + PROCESS (Sébastien) voient d'un clic les files et l'activité de l'un
 * seulement. Ne donne aucun métier en plus : on choisit DANS son périmètre.
 * L'administrateur, dont le périmètre est tous les métiers, choisit parmi les
 * quatre (19/10).
 */
function SelecteurMetiers({
  metiers,
  choisi,
  onChoisir,
}: {
  metiers: readonly ClePerimetre[]
  choisi: ClePerimetre | null
  onChoisir: (m: ClePerimetre | null) => void
}): JSX.Element {
  const options: { cle: ClePerimetre | null; libelle: string }[] = [
    { cle: null, libelle: 'Tous' },
    ...metiers.map((m) => ({ cle: m, libelle: libelleCle(m) })),
  ]
  // Six choix pour l'administrateur (quatre métiers, Goulotte, Tous) : plus
  // larges qu'un téléphone. La rangée défile au doigt au lieu d'élargir la
  // page ; le choix retenu est ramené en vue à l'ouverture.
  const rangee = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const r = rangee.current
    const actif = r?.querySelector<HTMLElement>('[aria-checked="true"]')
    if (!r || !actif) return
    if (actif.offsetLeft < r.scrollLeft || actif.offsetLeft + actif.offsetWidth > r.scrollLeft + r.clientWidth) {
      r.scrollLeft = actif.offsetLeft - 3
    }
  }, [choisi])
  return (
    <div ref={rangee} role="radiogroup" aria-label="Métier affiché" className="flex selecteur-metiers" style={{ backgroundColor: 'var(--color-gray-5)', borderRadius: 8, padding: 3, maxWidth: '100%', minWidth: 0 }}>
      {options.map((o) => {
        const actif = choisi === o.cle
        return (
          <button
            key={o.cle ?? 'tous'}
            type="button"
            role="radio"
            aria-checked={actif}
            onClick={() => onChoisir(o.cle)}
            className="inline-flex items-center"
            style={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              gap: 6,
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: actif ? 500 : 400,
              padding: '5px 10px',
              minHeight: 32,
              borderRadius: 6,
              border: actif ? '1px solid var(--color-black)' : '1px solid transparent',
              backgroundColor: actif ? 'var(--color-bg-card)' : 'transparent',
              cursor: 'pointer',
            }}
          >
            {o.cle && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: couleurCle(o.cle) }} />}
            {o.libelle}
          </button>
        )
      })}
    </div>
  )
}

function PuceMetier({ metier }: { metier: ClePerimetre }): JSX.Element {
  return (
    <span
      className="inline-flex items-center"
      style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 10px', borderRadius: 14, backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
    >
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: couleurCle(metier) }} />
      {libelleCle(metier)}
    </span>
  )
}
